// Donor-scope receipts API helper — CHG-1513 portal-pivot retarget,
// CHG-2947 (Slice F, DP-R5) server-side paging.
//
// Backend: portal.py canonical at GET /api/v1/my-giving/receipts.
// CHG-2947 wired the EXISTING (CHG-1481/CHG-2934 refund-aware)
// GivingService.get_receipts_paged to the route, param-presence-switched:
// sending page/per_page returns the donor-portal FLAT paged envelope
// ({data, page, per_page, total, total_pages}) with a server-side org_id
// filter; sending nothing keeps the legacy bare array (v1 api.ts +
// mobile unchanged). This helper ALWAYS pages on the server and
// normalizes through the CHG-2916 list-contract module (CHG-2947
// flat-envelope branch).
//
// reissueMyReceiptPdf is BACK (CHG-2949, Slice G / DP-R4, §8 decision 4 —
// DONOR-FACING): POST /api/v1/my-giving/receipts/{id}/reissue. "ready"
// returns the rendered pdf_url directly; "regenerating" means a render
// was enqueued (missing/failed PDF, or a voided/partially_refunded
// receipt force-regenerating so the artifact carries the VOID/partial
// banners) — refetch the list until pdf_url repopulates. Rate-limited
// server-side (5/day per account → 429).
//
// Not sent (no such params server-side — dropped no-ops): search,
// sortField, sortDirection.

import { apiClient } from "./client";
import {
  listPageParams,
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";

export interface MyReceipt {
  id: string;
  donation_id: string;
  org_name: string | null;
  org_logo_url: string | null;
  receipt_number: string;
  // CHG-2912/CHG-2934 — NET deductible amount (gross minus refunded);
  // 0 for a voided (fully refunded) receipt.
  amount_minor_units: number;
  currency: string;
  issued_at: string;
  delivery_channel: string;
  delivery_status: string;
  pdf_url: string | null;
  // CHG-2934 (DP-R13.3) — tax-validity state (valid | voided |
  // partially_refunded) + cumulative refunded amount so the page badges
  // refunded receipts instead of showing an unexplained reduced amount.
  status: string;
  refunded_amount_minor_units: number;
}

export interface ListMyReceiptsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  /** Server-side org filter — CHG-2947. */
  org_id?: string;
}

export async function listMyReceipts(
  params: ListMyReceiptsParams = {},
): Promise<NormalizedList<MyReceipt>> {
  const { page, per_page } = listPageParams(params);
  const r = await apiClient().get<RawListEnvelope<MyReceipt>>(
    "/my-giving/receipts",
    {
      params: {
        page,
        per_page,
        org_id: params.org_id,
      },
    },
  );
  return normalizeListResponse<MyReceipt>(r.data);
}

// CHG-2949 (Slice G) — donor-scoped receipt PDF reissue. Mirrors
// PortalReceiptPdfResponse (src/api/app/schemas/portal.py): status is
// "ready" (pdf_url populated) or "regenerating" (pdf_url null; render
// enqueued — poll the list). Voided/partially_refunded receipts never
// return "ready" (server force-regenerates a possibly-stale artifact).
export interface ReceiptPdfReissueResponse {
  pdf_url: string | null;
  status: "ready" | "regenerating";
}

export async function reissueMyReceiptPdf(
  receiptId: string,
): Promise<ReceiptPdfReissueResponse> {
  const r = await apiClient().post<ReceiptPdfReissueResponse>(
    `/my-giving/receipts/${receiptId}/reissue`,
  );
  return r.data;
}

export const meReceiptsApi = {
  listMyReceipts,
  reissueMyReceiptPdf,
};
